import { GuestsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as GuestsActions from './guests.actions';

@Injectable()
export class GuestsEffects {
  private actions$ = inject(Actions);
  private guestsApi = inject(GuestsApiService);
  private notification = inject(NotificationService);

  loadGuests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.loadGuests),
      switchMap(({ params }) =>
        this.guestsApi.findAll(params).pipe(
          map((response) => GuestsActions.loadGuestsSuccess({
            guests: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_guests_error');
            return of(GuestsActions.loadGuestsFailure({ error: error.message || 'Failed to load guests' }));
          }),
        ),
      ),
    ),
  );

  createGuest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.createGuest),
      switchMap(({ guestData }) =>
        this.guestsApi.create(guestData).pipe(
          map((guest) => {
            this.notification.success('admin.notifications.guest_created');
            return GuestsActions.createGuestSuccess({ guest });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_guest_error');
            return of(GuestsActions.createGuestFailure({ error: error.message || 'Failed to create guest' }));
          }),
        ),
      ),
    ),
  );

  updateGuest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.updateGuest),
      switchMap(({ id, guestData }) =>
        this.guestsApi.update(id, guestData).pipe(
          map((guest) => {
            this.notification.success('admin.notifications.guest_updated');
            return GuestsActions.updateGuestSuccess({ guest });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_guest_error');
            return of(GuestsActions.updateGuestFailure({ error: error.message || 'Failed to update guest' }));
          }),
        ),
      ),
    ),
  );

  deleteGuest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.deleteGuest),
      switchMap(({ id }) =>
        this.guestsApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.guest_deleted');
            return GuestsActions.deleteGuestSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_guest_error');
            return of(GuestsActions.deleteGuestFailure({ error: error.message || 'Failed to delete guest' }));
          }),
        ),
      ),
    ),
  );

  uploadGuestPhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.uploadGuestPhoto),
      switchMap(({ id, file }) =>
        this.guestsApi.uploadPhoto(id, file).pipe(
          map((guest) => {
            this.notification.success('admin.notifications.guest_photo_uploaded');
            return GuestsActions.uploadGuestPhotoSuccess({ guest });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.upload_guest_photo_error');
            return of(GuestsActions.uploadGuestPhotoFailure({ error: error.message || 'Failed to upload photo' }));
          }),
        ),
      ),
    ),
  );

  deleteGuestPhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.deleteGuestPhoto),
      switchMap(({ id }) =>
        this.guestsApi.deletePhoto(id).pipe(
          map((guest) => {
            this.notification.success('admin.notifications.guest_photo_deleted');
            return GuestsActions.deleteGuestPhotoSuccess({ guest });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_guest_photo_error');
            return of(GuestsActions.deleteGuestPhotoFailure({ error: error.message || 'Failed to delete photo' }));
          }),
        ),
      ),
    ),
  );
}
